import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts/core";
import { TreemapChart } from "echarts/charts";
import { TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsOption } from "echarts";

import type { HeatmapNode } from "../types";

echarts.use([TreemapChart, TooltipComponent, CanvasRenderer]);

export type MarketTab = "Overview";

export type MapFilter = "S&P 500";

function clamp(x: number, min: number, max: number) {
  return Math.max(min, Math.min(max, x));
}

function toHex(n: number) {
  const s = Math.round(n).toString(16).padStart(2, "0");
  return s;
}

function rgbToHex(r: number, g: number, b: number) {
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

/**
 * Finviz-like diverging heat color.
 * - Neutral: dark gray
 * - Positive: green intensity increases
 * - Negative: red intensity increases
 */
function chgToColor(chg?: number | null) {
  // Palette chosen for a dark UI. Adjust as needed.
  const neutral = [48, 54, 64]; // dark gray
  const pos = [0, 200, 90]; // green
  const neg = [230, 70, 70]; // red

  if (chg == null || Number.isNaN(chg)) return rgbToHex(neutral[0], neutral[1], neutral[2]);

  // Clamp to typical daily range; you can make this dynamic from the dataset if desired.
  const maxAbs = 5; // percent
  const v = clamp(chg, -maxAbs, maxAbs) / maxAbs;

  if (Math.abs(v) < 1e-6) return rgbToHex(neutral[0], neutral[1], neutral[2]);

  const t = Math.abs(v);
  const target = v > 0 ? pos : neg;
  const r = lerp(neutral[0], target[0], t);
  const g = lerp(neutral[1], target[1], t);
  const b = lerp(neutral[2], target[2], t);
  return rgbToHex(r, g, b);
}

function formatPct(x?: number | null) {
  if (x == null || Number.isNaN(x)) return "—";
  const sign = x > 0 ? "+" : "";
  return `${sign}${x.toFixed(2)}%`;
}

function inferredValue(node: HeatmapNode): number {
  if (typeof node.value === "number" && node.value > 0) return node.value;
  const kids = node.children ?? [];
  if (kids.length === 0) return 1;
  return kids.reduce((sum, c) => sum + inferredValue(c), 0);
}

function transformForTreemap(node: HeatmapNode): any {
  const value = inferredValue(node);
  const chg = node.changePct ?? null;

  const base: any = {
    name: node.name,
    value,
    chg,
    itemStyle: {
      color: chgToColor(chg),
      borderColor: "#0f141a",
      borderWidth: 1,
      gapWidth: 1,
    },
  };

  if (node.children && node.children.length > 0) {
    base.children = node.children.map(transformForTreemap);
  }

  return base;
}

export type MarketHeatmapCardProps = {
  heatmap: HeatmapNode | null;
  tab: MarketTab;
  setTab: (t: MarketTab) => void;
  mapFilter: MapFilter;
  setMapFilter: (v: MapFilter) => void;
  className?: string;
};

export function MarketHeatmapCard({
  heatmap,
  tab,
  setTab,
  mapFilter,
  setMapFilter,
  className,
}: MarketHeatmapCardProps) {
  const chartDomRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<echarts.EChartsType | null>(null);

  // Init chart once
  useEffect(() => {
    if (!chartDomRef.current) return;

    const dom = chartDomRef.current;
    const instance = echarts.init(dom, undefined, { renderer: "canvas" });
    chartRef.current = instance;

    const ro = new ResizeObserver(() => instance.resize());
    ro.observe(dom);

    return () => {
      ro.disconnect();
      instance.dispose();
      chartRef.current = null;
    };
  }, []);

  const option: EChartsOption | null = useMemo(() => {
    if (!heatmap) return null;

    const opt: EChartsOption = {
      backgroundColor: "transparent",
      animation: false,
      tooltip: {
        trigger: "item",
        confine: true,
        backgroundColor: "rgba(2, 6, 23, 0.92)",
        borderWidth: 0,
        padding: 10,
        textStyle: { color: "#e5e7eb", fontSize: 12 },
        formatter: (params: any) => {
          const path = (params?.treePathInfo ?? [])
            .map((p: any) => p?.name)
            .filter(Boolean)
            .join(" / ");

          const chg = params?.data?.chg;
          const val = params?.data?.value;

          return `
            <div style="font-size:12px;">
              <div style="margin-bottom:6px; font-weight:600;">${path || params?.name || ""}</div>
              <div style="display:flex; justify-content:space-between; gap:12px;">
                <div style="opacity:0.75;">Change</div>
                <div style="font-weight:600; font-variant-numeric: tabular-nums;">${formatPct(chg)}</div>
              </div>
              <div style="display:flex; justify-content:space-between; gap:12px;">
                <div style="opacity:0.75;">Weight</div>
                <div style="font-weight:600; font-variant-numeric: tabular-nums;">${
                  typeof val === "number" ? Math.round(val).toLocaleString() : "—"
                }</div>
              </div>
            </div>
          `;
        },
      },
      series: [
        {
          type: "treemap",
          data: [transformForTreemap(heatmap)],
          roam: true,
          nodeClick: false,
          breadcrumb: { show: false },
          upperLabel: {
            show: true,
            height: 18,
            color: "rgba(226, 232, 240, 0.85)",
            fontSize: 12,
            overflow: "truncate",
          },
          label: {
            show: true,
            color: "#ffffff",
            fontSize: 12,
            overflow: "truncate",
            formatter: (p: any) => {
              const isLeaf = !p?.data?.children || p.data.children.length === 0;
              if (!isLeaf) return p?.name ?? "";
              const chg = p?.data?.chg;
              return `${p?.name ?? ""}\n${formatPct(chg)}`;
            },
          },
          levels: [
            {
              itemStyle: { borderColor: "#0b0f14", borderWidth: 3, gapWidth: 2 },
              upperLabel: { show: true },
            },
            {
              itemStyle: { borderColor: "#0b0f14", borderWidth: 2, gapWidth: 1 },
              upperLabel: { show: true },
            },
            {
              itemStyle: { borderColor: "#0f141a", borderWidth: 1, gapWidth: 1 },
            },
          ],
          labelLayout: { hideOverlap: true },
        },
      ],
    };

    return opt;
  }, [heatmap]);

  // Apply option to chart
  useEffect(() => {
    if (!chartRef.current) return;
    if (!option) {
      chartRef.current.clear();
      return;
    }
    chartRef.current.setOption(option, { notMerge: true, lazyUpdate: true });
  }, [option]);

  return (
    <div
      className={
        "rounded-xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.03] p-5 text-white shadow-[0_14px_40px_-26px_rgba(0,0,0,0.8)] " +
        (className ?? "")
      }
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-sm font-semibold tracking-wide text-white/85">
            Market Heatmap
          </div>
          <div className="mt-2 text-xs text-white/60">
            {tab} · {mapFilter}
          </div>
        </div>
      </div>

      {/* Controls (kept minimal; state is owned by the page) */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap items-center gap-2 rounded-full border border-white/10 bg-white/5 p-1">
          <button
            type="button"
            onClick={() => setTab("Overview")}
            className={
              "rounded-full px-3 py-1 text-[11px] font-semibold tracking-wide transition-colors " +
              (tab === "Overview" ? "bg-white/10 text-white" : "text-white/60 hover:text-white")
            }
          >
            Overview
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 rounded-full border border-white/10 bg-white/5 p-1">
          <button
            type="button"
            onClick={() => setMapFilter("S&P 500")}
            className={
              "rounded-full px-3 py-1 text-[11px] font-semibold tracking-wide transition-colors " +
              (mapFilter === "S&P 500" ? "bg-white/10 text-white" : "text-white/60 hover:text-white")
            }
          >
            S&P 500
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-2">
        <div className="relative h-[520px] w-full">
          <div ref={chartDomRef} className="absolute inset-0" />

          {!heatmap ? (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-white/65">
              Loading…
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-white/50">
        <div className="min-w-0 truncate">&nbsp;</div>
        <div className="flex items-center gap-3">
          <span>Red: down</span>
          <span>Gray: flat</span>
          <span>Green: up</span>
        </div>
      </div>
    </div>
  );
}

export default MarketHeatmapCard;
